import { z } from "zod";
import {
  workItemDispositionSchema,
  workQueueItemKindSchema,
} from "./work-queue";

// TES-1207 — the coach's Historik. A-01:
// "Historik ingår i MVP och ska visa tidigare coachkontakter, formulär, återkopplingar, överenskommelser och uppföljningar i begriplig tidsordning."
//
// A read model over rows that already exist (the coach chat, the coach's
// check-in handling, work-item dispositions, follow-ups, Patientminnet's
// agreements, handoffs). Nothing is stored, so it cannot drift from the
// surfaces that wrote them.
//
// The medical boundary: every `text` on the wire is something a coach wrote
// herself (her message, her close reason, her follow-up prompt) or an
// agreement she can already read in Patientminnet. A check-in routed to the
// doctor has no row here at all, a closed case is listed only for the coach's
// own item kinds, and a handoff row carries its direction and date, never its
// reason, context or resolution.

export const COACH_HISTORY_KINDS = [
  // A message a named coach wrote in the coach chat. Automated coach-persona
  // messages (no sender) are not a contact anyone made.
  "coach_message",
  // A weekly check-in the coach answered ("Skicka svar och slutför").
  "checkin_answered",
  // A weekly check-in the coach closed without replying, with her reason.
  "checkin_closed",
  // A coach work-queue case closed, with its outcome and reason.
  "case_closed",
  // A follow-up the coach marked done.
  "follow_up_completed",
  // An agreement in Patientminnet.
  "agreement",
  // A handoff to or from the coach that was resolved.
  "handoff_resolved",
] as const;
export const coachHistoryKindSchema = z.enum(COACH_HISTORY_KINDS);
export type CoachHistoryKind = z.infer<typeof coachHistoryKindSchema>;

// The work-queue kinds whose dispositions are coach contacts. Every other kind
// (renewals, labs, medical reviews, …) is a doctor's or admin's case and never
// reaches this list. A check-in's handling has its own rows
// (`checkin_answered` / `checkin_closed`).
export const COACH_HISTORY_CASE_KINDS = [
  "needs_follow_up_contact",
  "coach_welcome",
  "follow_up",
] as const;

export const coachHistoryRowSchema = z.object({
  // `<kind>:<source row id>` — unique across the merged list.
  id: z.string(),
  at: z.string(),
  kind: coachHistoryKindSchema,
  // The coach's own words, or the agreement's text. Null when the row has
  // nothing to quote.
  text: z.string().nullable(),
  // The staff member who did it, by display name. Null when the row has no
  // person (a system close) or the name is not on file.
  by: z.string().nullable(),
  // `checkin_*`: when the check-in was submitted, for "Veckoincheckning ·
  // vecka NN".
  submittedAt: z.string().optional(),
  // `case_closed`: which case and how it ended.
  itemKind: workQueueItemKindSchema.optional(),
  disposition: workItemDispositionSchema.optional(),
  // `handoff_resolved`: which way it went.
  handoffDirection: z.enum(["to_coach", "from_coach"]).optional(),
});
export type CoachHistoryRow = z.infer<typeof coachHistoryRowSchema>;

export const coachHistorySchema = z.object({
  patientId: z.string(),
  // Newest first.
  rows: z.array(coachHistoryRowSchema),
});
export type CoachHistory = z.infer<typeof coachHistorySchema>;
